import { useEffect, useMemo, useState, lazy, Suspense, type PropsWithChildren } from "react";
import setColorsFromThemeJsonTS from "./app/layout/css/theme";
import {
  BrowserRouter as Router,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./app/store/configureStore";
import {
  setApplicationSettings,
  loadEnterprise,
  setEnterpriseDetail,
} from "./modules/common/settingsSlice";
import * as searchActions from "./modules/facilities/components/search/searchSlice";
import { PreLoader } from "./modules/loader/Loader";
import { setFacilityPreCartDetail } from "./modules/facilities/components/facilityPreCart/facilityPreCartSlice";
import localFeatures from "virtual:local-features";
import {
  FeatureManagementProvider,
  type FeatureManagementAttributes,
  type FeatureManagementConfig,
  useTreatment,
} from "./app/feature-management";
const RouteComponent = lazy(() => import("./modules/Route"));

const LocationWrapper = ({ children }: any) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const search = useAppSelector((state) => state.search);
  const navigationType = useNavigationType();
  const common = useAppSelector((state) => state.common);
  const authentication = useAppSelector((state) => state.authentication);
  const removePreCartData = () => {
    if (
      !location.pathname.includes("SelectReservationPreCart") &&
      !location.pathname.includes("park/")
    ) {
      localStorage.removeItem("preCartData");
      localStorage.removeItem("vehicleData");
      localStorage.removeItem("dynamicVehicleData");
      dispatch(setFacilityPreCartDetail(null));
    }
    if (
      location.pathname.includes("park/") ||
      location.pathname.includes("results")
    ) {
    } else {
      localStorage.removeItem("tempPreCartData");
    }

    if (
      location.pathname.includes("park/") ||
      location.pathname.includes("results") ||
      location.pathname.includes("SelectReservationPreCart") || location.pathname === "/" && search.searchStep === "homeSearch"
    ) {
    } else {
      let searchData = {
        startDate: location.pathname.includes("ShoppingCart")
          ? new Date(sessionStorage.getItem("selectedStartdate") || "")
          : null,
        endDate: location.pathname.includes("ShoppingCart")
          ? new Date(sessionStorage.getItem("selectedEnddate") || "")
          : null,
        nights: location.pathname.includes("ShoppingCart")
          ? sessionStorage.getItem("selectedNights")
            ? sessionStorage.getItem("selectedNights")
            : 0
          : 0,
        unitCategoryId: null,
        sleepingUnitId: null,
        minVehicleLength: null,
        unitTypesGroupIds: [],
        isADA: false,
        amenitiesIds: [],
        isAdditionalFilterShow: false,
        sort: common.applicationSettings?.defaultParkListSort,
        unitSortBy: common.applicationSettings?.defaultUnitSort,
        placeName: location.pathname.includes("ShoppingCart")
          ? search.selectedPlace?.Name
          : "",
      };
      dispatch(searchActions.setSearchData(searchData));
      if (!location.pathname.includes("ShoppingCart")) {
        dispatch(searchActions.setSelectedPlace(null));
      }
    }
  };
  const removeSignPreCartData = () => {
    if (
      !location.pathname.includes("SelectSignUpPreCart") &&
      !location.pathname.includes("Details") &&
      !location.pathname.includes("TellAFriend") &&
      (localStorage.getItem("addDependentFromActivitySignUpPrecart") ===
        "false" ||
        localStorage.getItem("addDependentFromActivitySignUpPrecart") === "")
    ) {
      localStorage.removeItem("ActivitySignUp");
      localStorage.removeItem("ActivitySessionID");
    }
  };

  const removeCustomerId = () => {
    if (
      !location.pathname.includes("Customers/Application") &&
      !location.pathname.includes("Customers/CustomerApplication") &&
      !location.pathname.includes("Customers/ApplicationStatus") &&
      localStorage.getItem("applicationCustomerId")
    ) {
      localStorage.removeItem("applicationCustomerId");
    }
  };
  useEffect(() => {
    if (
      authentication.isLoggedIn &&
      location.pathname.includes("login/callback")
    ) {
      // get cookie lastUrl
      const lastUrl = localStorage.getItem("login_redirect_url");
      if (lastUrl) {
        navigate(lastUrl, { replace: true });
        localStorage.removeItem("login_redirect_url");
      }

      if (navigationType === "POP") {
        navigate("/", { replace: true });
        window.location.reload();
      }
    }
  }, [navigationType, location.pathname, navigate]);

  useEffect(() => {
    document.getElementById("content")?.focus();
    removePreCartData();
    removeSignPreCartData();
    removeCustomerId();
  }, [location.pathname]);
  return children;
};

const FeatureManagementGate = ({ children }: PropsWithChildren) => {
  const common = useAppSelector((state) => state.common);
  const authentication = useAppSelector((state) => state.authentication);

  const config = useMemo<FeatureManagementConfig>(() => {
    const runtimeConfig = common.applicationSettings?.featureManagement ?? {};
    const useLocalhost =
      import.meta.env.VITE_FME_LOCAL === "true" || runtimeConfig.useLocalhost === true;
    const sdkKey =
      import.meta.env.VITE_FME_BROWSER_SDK_KEY ||
      common.webSiteSettings?.fme_browser_sdk_key ||
      "";
    const userKey =
      authentication.customerId > 0
        ? `customer:${authentication.customerId}`
        : common.enterpriseDetail?.installIdentity || "anonymous";

    return {
      sdkKey,
      userKey,
      useLocalhost,
      localFeatures,
      streamingEnabled: runtimeConfig.streamingEnabled !== false,
      urls: runtimeConfig.urls,
    };
  }, [
    authentication.customerId,
    common.applicationSettings?.featureManagement,
    common.enterpriseDetail?.installIdentity,
    common.webSiteSettings?.fme_browser_sdk_key,
  ]);

  const attributes = useMemo<FeatureManagementAttributes>(() => {
    const values: FeatureManagementAttributes = {};
    if (common.enterpriseDetail?.instanceName) {
      values.tenantId = common.enterpriseDetail.instanceName;
    }
    if (common.enterpriseDetail?.installIdentity) {
      values.installIdentity = common.enterpriseDetail.installIdentity;
    }
    if (common.enterpriseDetail?.webStoreId) {
      values.webStoreId = common.enterpriseDetail.webStoreId;
    }
    if (authentication.customerId > 0) {
      values.customerId = authentication.customerId;
    }

    return values;
  }, [
    authentication.customerId,
    common.enterpriseDetail?.installIdentity,
    common.enterpriseDetail?.instanceName,
    common.enterpriseDetail?.webStoreId,
  ]);

  return (
    <FeatureManagementProvider config={config} attributes={attributes}>
      {children}
    </FeatureManagementProvider>
  );
};

const MAX_INIT_RETRIES = 3;

const ThemeClassHandler = () => {
  const common = useAppSelector((state) => state.common);
  const { treatment, isReady } = useTreatment({ name: "client-theme-css-scope" });

  useEffect(() => {
    if (isReady && common.enterpriseDetail?.enterpriseName) {
      const body = document.getElementsByTagName('body')[0];
      const enterpriseName = common.enterpriseDetail.enterpriseName;

      body.classList.remove(enterpriseName, 'client-theme');
      const themeClass = treatment === 'on' ? 'client-theme' : enterpriseName;
      body.classList.add(themeClass);
    }
  }, [treatment, isReady, common.enterpriseDetail?.enterpriseName]);

  return null;
};


function App() {
  const [baseName, setBaseName] = useState("");
  const [initError, setInitError] = useState(false);
  const dispatch = useAppDispatch();
  const common = useAppSelector((state) => state.common);

  useEffect(() => {
    const initializeApp = async (attempt = 1): Promise<void> => {
      try {
        const response = await fetch("config.json");
        if (!response.ok) throw new Error(`config.json returned ${response.status}`);
        const data = await response.json();

        await new Promise<void>((resolve, reject) => {
          loadEnterprise((enterprise: any) => {
            if (enterprise) {
              setColorsFromThemeJsonTS(enterprise.enterpriseName);
              data.installIdentity = enterprise.installIdentity;
              data.webStoreId = enterprise.webStoreId;
              data.imageBaseUrl = enterprise.cdnURL ? enterprise.cdnURL : "";
              dispatch(setApplicationSettings(data));
              dispatch(setEnterpriseDetail(enterprise));
              const faviconUrl = `/themes/${enterprise.enterpriseName}/favicon.ico`;
              const favIcon = document.getElementById(
                "favIcon"
              ) as HTMLLinkElement | null;
              if (favIcon) {
                favIcon.href = faviconUrl;
              }
              resolve();
            } else {
              reject(new Error("Enterprise data was empty"));
            }
          }).catch(reject);
        });
      } catch (error) {
        console.error(`App initialization attempt ${attempt} failed:`, error);
        if (attempt < MAX_INIT_RETRIES) {
          await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
          return initializeApp(attempt + 1);
        }
        setInitError(true);
      }
    };

    initializeApp();
    setTimeout(() => {
      setBaseName((window as any).instanceName);
    }, 0);
  }, []);

  return (
    <Suspense fallback={<PreLoader isPreLoaderShow={true} />}>
      {/* <ErrorBoundary> */}
      <Router basename={baseName}>
        <LocationWrapper>
          {initError ? (
            <div className="pre-loader" role="alert">
              <p className="loader-large-text">
                We're having trouble loading the application.
              </p>
              <p className="loader-small-text">
                Please check your connection and try again.
              </p>
              <button
                className="btn btn-primary mt-3"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : common.applicationSettings && common.enterpriseDetail ? (
            <FeatureManagementGate>
            <ThemeClassHandler />
              <RouteComponent />
            </FeatureManagementGate>
          ) : (
            <PreLoader isPreLoaderShow={true} />
          )}
        </LocationWrapper>
      </Router>
      {/* </ErrorBoundary> */}
    </Suspense>
  );
}

export default App;
